import { useCallback, useMemo } from "react";
import { useLabels } from "../api/hooks";
import { useNetworkStore } from "../stores/network";
import { useMyLabels, type MyLabel } from "../stores/my-labels";
import { abbreviateHex } from "../components/TxTable";

export type LabelSource = "private" | "shared";

export interface ResolvedLabel {
  label: string;
  source: LabelSource;
}

/**
 * Internal helper: select the per-network list of private labels with a stable
 * reference. Selecting `s.labels[id] ?? []` directly inside the zustand
 * selector returns a fresh `[]` every render and triggers an infinite loop
 * (React reports "getSnapshot should be cached"); selecting the map and
 * deriving the list via useMemo keeps identity stable.
 */
function useMyLabelsForNetwork(): MyLabel[] {
  const { selectedNetwork } = useNetworkStore();
  const labelsMap = useMyLabels((s) => s.labels);
  return useMemo(
    () => labelsMap[selectedNetwork] ?? [],
    [labelsMap, selectedNetwork],
  );
}

/**
 * Returns a function that resolves an address to its label if one exists.
 * Falls back to a truncated address if no label is found.
 *
 * Private (local) labels override shared (server) labels.
 */
export function useAddressResolver() {
  const { selectedNetwork } = useNetworkStore();
  const { data: labels } = useLabels(selectedNetwork);
  const myLabels = useMyLabelsForNetwork();

  const resolve = useCallback(
    (address: string): string => {
      const lc = address.toLowerCase();
      const mine = myLabels.find((l) => l.address === lc);
      if (mine) return `${mine.label} (${abbreviateHex(address)})`;
      const shared = labels?.find((l) => l.address.toLowerCase() === lc);
      if (shared) return `${shared.label} (${abbreviateHex(address)})`;
      return abbreviateHex(address);
    },
    [labels, myLabels],
  );

  return resolve;
}

/**
 * Returns a function that resolves an address to just its label name, or undefined.
 *
 * Private (local) labels override shared (server) labels.
 */
export function useLabelResolver() {
  const { selectedNetwork } = useNetworkStore();
  const { data: labels } = useLabels(selectedNetwork);
  const myLabels = useMyLabelsForNetwork();

  return useCallback(
    (address: string): string | undefined => {
      const lc = address.toLowerCase();
      const mine = myLabels.find((l) => l.address === lc);
      if (mine) return mine.label;
      const shared = labels?.find((l) => l.address.toLowerCase() === lc);
      return shared?.label;
    },
    [labels, myLabels],
  );
}

/**
 * Returns a function that resolves an address to its label + source, or undefined.
 * Use this when callers need to style the label differently based on its origin
 * (e.g. show a private label in a different color than a shared one).
 */
export function useLabelSource() {
  const { selectedNetwork } = useNetworkStore();
  const { data: labels } = useLabels(selectedNetwork);
  const myLabels = useMyLabelsForNetwork();

  return useCallback(
    (address: string): ResolvedLabel | undefined => {
      const lc = address.toLowerCase();
      const mine = myLabels.find((l) => l.address === lc);
      if (mine) return { label: mine.label, source: "private" };
      const shared = labels?.find((l) => l.address.toLowerCase() === lc);
      if (shared) return { label: shared.label, source: "shared" };
      return undefined;
    },
    [labels, myLabels],
  );
}

/**
 * Returns a Set of lowercase addresses that have labels (private or shared).
 */
export function useLabeledAddresses(): Set<string> {
  const { selectedNetwork } = useNetworkStore();
  const { data: labels } = useLabels(selectedNetwork);
  const myLabels = useMyLabelsForNetwork();

  return useMemo(() => {
    const set = new Set<string>();
    if (labels) {
      for (const l of labels) {
        set.add(l.address.toLowerCase());
      }
    }
    for (const l of myLabels) {
      set.add(l.address);
    }
    return set;
  }, [labels, myLabels]);
}
